import { Component, ChangeDetectionStrategy, inject, signal, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

export interface AnchorReviewRow {
  kind: 'path' | 'leaf';
  fieldName: string;
  value: unknown;
}

export interface PathWarning {
  fieldName: string;
  extractedValue: string;
  expectedValue: string;
}

export interface AnchorReviewData {
  pathRows: AnchorReviewRow[];
  leafRows: AnchorReviewRow[];
  warnings: PathWarning[];
}

export interface AnchorReviewDialogData {
  templateName: string;
  leafLabel: string;
  /** Runs the scoped extraction; resolves with the anchor values. Used for both the initial load and re-extraction. */
  extract: () => Promise<Record<string, unknown>>;
  /** Builds the review rows and path warnings from the extracted anchors. */
  buildReview: (anchors: Record<string, unknown>) => AnchorReviewData;
}

export interface AnchorReviewDialogResult {
  confirmed: boolean;
  anchors: Record<string, unknown>;
}

/**
 * Read-only review of the anchors extracted at template assignment (Q9-B).
 * The author can Confirm & Save, Re-extract, or Cancel — values cannot be
 * edited manually: if a value is wrong the fix is to adjust the anchor-field
 * selection, not the value.
 */
@Component({
  selector: 'app-anchor-review-dialog',
  imports: [MatDialogModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="p-6 w-full" data-testid="anchor-review-dialog">
      <!-- Header -->
      <div class="flex items-start gap-3 mb-4">
        <div class="rounded-full bg-indigo-50 dark:bg-indigo-900/30 p-2 shrink-0">
          <mat-icon class="!text-xl text-indigo-600 dark:text-indigo-400">anchor</mat-icon>
        </div>
        <div class="min-w-0">
          <h2 data-testid="anchor-review-heading" class="text-lg font-semibold text-zinc-900 dark:text-white leading-tight">
            Review extracted anchors
          </h2>
          <p class="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            "{{ data.templateName }}" → leaf "{{ data.leafLabel }}"
          </p>
        </div>
      </div>

      <!-- Q8 path warnings (non-blocking) -->
      @if (warnings().length > 0) {
        <div data-testid="anchor-review-warnings"
             class="mb-4 space-y-1.5 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2.5">
          @for (w of warnings(); track w.fieldName) {
            <div class="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-200">
              <mat-icon class="!text-sm !w-4 !h-4 !leading-4 shrink-0">warning</mat-icon>
              <span>
                <strong>{{ w.fieldName }}</strong> extracted as "{{ w.extractedValue }}" but this leaf's path expects
                "{{ w.expectedValue }}". The placement may be wrong.
              </span>
            </div>
          }
        </div>
      }

      @if (isLoading() || isReextracting()) {
        <div class="flex items-center gap-3 py-8 justify-center text-sm text-zinc-500 dark:text-zinc-400" data-testid="anchor-review-spinner">
          <div class="animate-spin rounded-full h-5 w-5 border-2 border-zinc-300 dark:border-zinc-500 border-t-indigo-500"></div>
          {{ isLoading() ? 'Loading anchors…' : 'Re-extracting anchors…' }}
        </div>
      } @else {
        <!-- Path anchors -->
        @if (pathRows().length > 0) {
          <h3 class="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-1.5">
            Path anchors <span class="font-normal normal-case">(UI only — not scored)</span>
          </h3>
          <div class="mb-4 rounded-md border border-zinc-200 dark:border-zinc-700 divide-y divide-zinc-100 dark:divide-zinc-700">
            @for (row of pathRows(); track row.fieldName) {
              <div class="flex items-center justify-between gap-3 px-3 py-2 text-sm" [attr.data-testid]="'anchor-row-' + row.fieldName">
                <span class="text-zinc-600 dark:text-zinc-400 truncate">{{ row.fieldName }}</span>
                <span class="font-medium text-zinc-900 dark:text-zinc-100 truncate">{{ formatValue(row.value) }}</span>
              </div>
            }
          </div>
        }

        <!-- Leaf anchors -->
        <h3 class="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-1.5">
          Leaf anchors <span class="font-normal normal-case">(scored at matching)</span>
        </h3>
        <div class="mb-4 rounded-md border border-indigo-200 dark:border-indigo-800 divide-y divide-indigo-100 dark:divide-indigo-800/60">
          @for (row of leafRows(); track row.fieldName) {
            <div class="flex items-center justify-between gap-3 px-3 py-2 text-sm" [attr.data-testid]="'anchor-row-' + row.fieldName">
              <span class="text-zinc-600 dark:text-zinc-400 truncate">{{ row.fieldName }}</span>
              <span class="font-medium text-zinc-900 dark:text-zinc-100 truncate">{{ formatValue(row.value) }}</span>
            </div>
          } @empty {
            <div class="px-3 py-2 text-sm text-zinc-400 dark:text-zinc-500">No leaf anchors configured on this leaf.</div>
          }
        </div>
      }

      <!-- Read-only note -->
      @if (!isLoading()) {
        <p class="mb-4 text-xs text-zinc-500 dark:text-zinc-400" data-testid="anchor-review-note">
          Values can't be edited manually. If a value looks wrong, re-extract — or adjust the leaf's
          anchor-field selection on the Anchor Tree page.
        </p>
      }

      @if (error()) {
        <p class="mb-4 text-xs text-red-600 dark:text-red-400" data-testid="anchor-review-error">{{ error() }}</p>
      }

      <!-- Actions -->
      <div class="flex justify-end gap-3 pt-2">
        <button
          data-testid="anchor-review-cancel-btn"
          type="button"
          class="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-600 transition-colors cursor-pointer"
          [disabled]="isReextracting()"
          (click)="cancel()"
        >
          Cancel
        </button>
        <button
          data-testid="anchor-review-reextract-btn"
          type="button"
          class="inline-flex items-center gap-1.5 justify-center rounded-md px-3 py-1.5 text-sm font-medium border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-600 shadow-sm transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          [disabled]="isLoading() || isReextracting()"
          (click)="reextract()"
        >
          <mat-icon class="!text-base !w-4 !h-4 !leading-4">refresh</mat-icon>
          Re-extract
        </button>
        <button
          data-testid="anchor-review-confirm-btn"
          type="button"
          class="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          [disabled]="isLoading() || isReextracting() || anchors === null"
          (click)="confirm()"
        >
          Confirm & Save
        </button>
      </div>
    </div>
  `
})
export class AnchorReviewDialogComponent implements OnInit {
  readonly dialogRef = inject(MatDialogRef<AnchorReviewDialogComponent, AnchorReviewDialogResult>);
  readonly data = inject<AnchorReviewDialogData>(MAT_DIALOG_DATA);

  readonly isLoading = signal(true);
  readonly isReextracting = signal(false);
  readonly error = signal<string | null>(null);
  readonly pathRows = signal<AnchorReviewRow[]>([]);
  readonly leafRows = signal<AnchorReviewRow[]>([]);
  readonly warnings = signal<PathWarning[]>([]);

  protected anchors: Record<string, unknown> | null = null;

  ngOnInit(): void {
    void this.loadAnchors();
  }

  confirm(): void {
    if (this.anchors === null) {
      return;
    }
    this.dialogRef.close({ confirmed: true, anchors: this.anchors });
  }

  cancel(): void {
    this.dialogRef.close({ confirmed: false, anchors: this.anchors ?? {} });
  }

  async reextract(): Promise<void> {
    this.isReextracting.set(true);
    try {
      await this.loadAnchors();
    } finally {
      this.isReextracting.set(false);
    }
  }

  private async loadAnchors(): Promise<void> {
    this.error.set(null);
    try {
      const anchors = await this.data.extract();
      this.anchors = anchors;
      const review = this.data.buildReview(anchors);
      this.pathRows.set(review.pathRows);
      this.leafRows.set(review.leafRows);
      this.warnings.set(review.warnings);
    } catch (err) {
      console.error('Anchor extraction failed:', err);
      this.error.set('Anchor extraction failed. Please try again.');
    } finally {
      this.isLoading.set(false);
    }
  }

  formatValue(value: unknown): string {
    if (value === undefined || value === null || value === '') {
      return '—';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
}

